import computeKpiDelta from '../../../helpers/compareHelpers/computeKpiDelta'

// AI-CONTEXT: See docs/features/compare-two-periods.md
// KPI compare view: two single numbers (primary period A vs compare period B)
// with the change between them. Chosen instead of ComparisonFrame's small
// multiples when the underlying chart is a KPI number — a delta reads far
// better than two lone numbers side by side.
//
// Presentational: the caller resolves valueA / valueB (the aggregate for each
// period) and passes them in. Δ is A - B; the % is relative to baseline B.
// Direction colours the change up(green) / down(red) / flat(grey) — this is a
// raw numeric direction, NOT a good/bad judgement (a metric can be
// negative-framed; leave that semantics to the caller if it matters).

/** @param {number|null|undefined} n @param {(n: number) => string} fmt */
const renderValue = (n, fmt) => (typeof n === 'number' && Number.isFinite(n) ? fmt(n) : '—')

/** @param {number} n @param {(n: number) => string} fmt — prefixes '+' for positive */
const signed = (n, fmt) => (n > 0 ? `+${fmt(n)}` : fmt(n))

const ARROW = { up: '▲', down: '▼', flat: '—' }

/**
 * @param {object} props
 * @param {number|null} props.valueA - Primary period value
 * @param {number|null} props.valueB - Compare period value
 * @param {string} props.labelA - Primary period label
 * @param {string} props.labelB - Compare period label
 * @param {(n: number) => string} [props.formatValue] - Formats a value/delta for display (default String)
 * @returns {JSX.Element}
 */
const KpiDeltaCompare = ({ valueA, valueB, labelA, labelB, formatValue = String }) => {
    const delta = computeKpiDelta(valueA, valueB)
    const hasDelta = delta.abs !== null

    return (
        <div className='kpi-delta-compare'>
            <div className='kpi-delta-compare-values'>
                <div className='kpi-delta-compare-value kpi-delta-compare-value--a'>
                    <span className='kpi-delta-compare-label'>{labelA}</span>
                    <span className='kpi-delta-compare-number'>{renderValue(valueA, formatValue)}</span>
                </div>
                <div className='kpi-delta-compare-value kpi-delta-compare-value--b'>
                    <span className='kpi-delta-compare-label'>{labelB}</span>
                    <span className='kpi-delta-compare-number'>{renderValue(valueB, formatValue)}</span>
                </div>
            </div>
            <div className={`kpi-delta-compare-delta kpi-delta-compare-delta--${delta.direction}`}>
                {hasDelta ? (
                    <>
                        <span className='kpi-delta-compare-arrow'>{ARROW[delta.direction]}</span>
                        <span className='kpi-delta-compare-abs'>{signed(delta.abs, formatValue)}</span>
                        {delta.pct !== null && (
                            <span className='kpi-delta-compare-pct'>
                                ({delta.pct > 0 ? '+' : ''}{delta.pct}%)
                            </span>
                        )}
                    </>
                ) : (
                    <span className='kpi-delta-compare-arrow'>—</span>
                )}
            </div>
        </div>
    )
}

export default KpiDeltaCompare
